import React from 'react';
import { Activity, CheckCircle2, Cpu, HelpCircle, XCircle } from 'lucide-react';
import { formatDateTime, formatUsd, relativeTime } from '../../utils/shipmentsFormat';

const Flag = ({ ok, yes, no }) => (
  ok
    ? <span style={{ color: '#1B5E20', display: 'inline-flex', alignItems: 'center', gap: 5 }}><CheckCircle2 size={15} aria-hidden="true" /> {yes}</span>
    : <span style={{ color: '#B71C1C', display: 'inline-flex', alignItems: 'center', gap: 5 }}><XCircle size={15} aria-hidden="true" /> {no}</span>
);

// env.googleConfigured is derived from the worker: true = fine, false = worker says the key is missing, null = unknown
// (no worker heartbeat yet): a NEUTRAL state, not a warning.
const GoogleState = ({ value }) => {
  if (value === true) return <Flag ok yes="Nastaveno" no="" />;
  if (value === false) {
    return (
      <span style={{ display: 'grid', gap: 4 }}>
        <Flag ok={false} yes="" no="Chybí přístupový klíč Google" />
        <span className="shp-small shp-muted">Soubor s klíčem Google musí správce nahrát na server (do služby synchronizace pošty).</span>
      </span>
    );
  }
  return (
    <span className="shp-muted" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <HelpCircle size={15} aria-hidden="true" /> Zatím neznámé – worker ještě neběžel
    </span>
  );
};

const CYCLE_LABELS = {
  at: 'Čas', mailbox: 'Schránka', mailboxes: 'Schránek', messagesSeen: 'Zpráv zkontrolováno', processed: 'Zpracováno', errors: 'Chyb', durationMs: 'Trvání (ms)',
};
const isPlain = (v) => v == null || ['string', 'number', 'boolean'].includes(typeof v);
const cycleValue = (k, v) => (k === 'at' && typeof v === 'string' ? formatDateTime(v) || v : String(v));

// lastCycleSummary may be a sentence, an object of counters or per-mailbox objects; everything is shown as plain text
function CycleSummary({ value }) {
  if (value == null || value === '') return <span className="shp-muted">zatím žádný cyklus neproběhl</span>;
  if (typeof value !== 'object') return <span className="shp-wrap">{String(value)}</span>;
  const entries = Object.entries(value);
  return (
    <ul style={{ margin: 0, paddingLeft: 18 }}>
      {entries.filter(([, v]) => isPlain(v)).map(([k, v]) => (
        <li key={k} className="shp-wrap"><span className="shp-muted">{CYCLE_LABELS[k] || k}:</span> {cycleValue(k, v)}</li>
      ))}
      {entries.filter(([, v]) => Array.isArray(v)).flatMap(([k, list]) => list.filter((x) => x && typeof x === 'object').map((item, i) => (
        <li key={`${k}-${i}`} className="shp-wrap">
          {Object.entries(item).filter(([, v]) => isPlain(v)).map(([ik, iv]) => `${CYCLE_LABELS[ik] || ik}: ${iv}`).join(', ')}
        </li>
      )))}
    </ul>
  );
}

// Worker status + last cycle + AI usage
export const WorkerCard = ({ worker, aiUsage, limit }) => {
  const calls = Number(aiUsage?.callsToday) || 0;
  return (
    <section className="shp-card" aria-labelledby="shp-worker-title">
      <h2 id="shp-worker-title" className="shp-h2"><Activity size={18} color="#E53935" aria-hidden="true" /> Stav synchronizace</h2>
      <dl className="shp-kv">
        <div><dt>Synchronizační služba</dt><dd><Flag ok={Boolean(worker?.healthy)} yes="Běží" no="Neběží" /></dd></div>
        <div><dt>Poslední signál života</dt><dd>{worker?.beatAt ? <time dateTime={worker.beatAt} title={formatDateTime(worker.beatAt)}>{relativeTime(worker.beatAt)}</time> : <span className="shp-muted">žádný</span>}</dd></div>
        <div style={{ gridColumn: '1 / -1' }}><dt>Poslední cyklus</dt><dd><CycleSummary value={worker?.lastCycleSummary} /></dd></div>
        <div>
          <dt>Volání AI dnes</dt>
          <dd className="shp-num">
            {calls}{limit ? ` z ${limit}` : ''}
            {limit ? <progress className="shp-progress" max={limit} value={Math.min(calls, limit)} aria-label="Využití denního limitu AI volání" style={{ display: 'block', marginTop: 4 }} /> : null}
          </dd>
        </div>
        <div><dt>Cena AI za posledních 30 dní</dt><dd className="shp-num">{formatUsd(aiUsage?.costLast30dUsd)}</dd></div>
      </dl>
    </section>
  );
};

// Read-only environment (set in the server .env, not editable here)
export const EnvCard = ({ env }) => (
  <section className="shp-card" aria-labelledby="shp-env-title">
    <h2 id="shp-env-title" className="shp-h2"><Cpu size={18} color="#E53935" aria-hidden="true" /> Prostředí serveru</h2>
    <p className="shp-muted shp-small" style={{ margin: '0 0 12px' }}>Tyto hodnoty se nastavují v souboru .env na serveru a v aplikaci je nelze měnit.</p>
    <dl className="shp-kv">
      <div><dt>Model pro třídění e-mailů</dt><dd className="shp-wrap">{env?.modelClassify || '–'}</dd></div>
      <div><dt>Model pro čtení údajů</dt><dd className="shp-wrap">{env?.modelExtract || '–'}</dd></div>
      <div><dt>Úsilí čtení údajů</dt><dd>{env?.extractEffort || '–'}</dd></div>
      <div><dt>Odesílání e-mailů (SMTP)</dt><dd><Flag ok={Boolean(env?.mailConfigured)} yes="Nastaveno" no="Nenastaveno" /></dd></div>
      <div><dt>Přístup ke Gmailu (Google)</dt><dd><GoogleState value={env?.googleConfigured} /></dd></div>
      <div>
        <dt>Příjemci oznámení</dt>
        <dd className="shp-wrap">{env?.notifyRecipients?.length ? env.notifyRecipients.join(', ') : <span className="shp-muted">nikdo</span>}</dd>
      </div>
    </dl>
  </section>
);
